import { create } from 'zustand';

/**
 * Préférence de thème (nuit/jour), locale à l'appareil.
 *
 * Le mécanisme de bascule existe déjà côté styles (`app.scss`) : le thème sombre est posé sur
 * `:root`, et l'attribut `data-carbon-theme="white"` sur la racine bascule vers le thème clair (et
 * l'accent clair). Ce store ne fait donc que **poser/retirer cet attribut** et mémoriser le choix.
 *
 * Défaut = **nuit** : l'application sert à examiner des images aériennes souvent sombres, où un fond
 * clair fausse la perception des contrastes (voir la note dans `app.scss`). Le thème clair reste
 * offert pour l'annotation en extérieur, où l'écran sombre devient illisible.
 *
 * Préférence uniquement (aucune donnée sensible) : `localStorage` est ici légitime, contrairement au
 * jeton d'accès qui, lui, reste hors de `localStorage` (voir `auth/store.ts`).
 */

export type ThemeMode = 'dark' | 'light';

const STORAGE_KEY = 'ddp-theme';

function readInitial(): ThemeMode {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    // Mode privé strict / stockage indisponible : on retombe sur le défaut, sans planter.
    return 'dark';
  }
}

/** Reflète le mode dans le DOM : l'attribut racine pilote les jetons Carbon et l'accent (app.scss). */
function applyToDocument(mode: ThemeMode): void {
  const root = document.documentElement;
  if (mode === 'light') {
    root.setAttribute('data-carbon-theme', 'white');
  } else {
    root.removeAttribute('data-carbon-theme');
  }
}

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: readInitial(),
  setMode: (mode) => {
    applyToDocument(mode);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Le choix ne survivra pas au rechargement, mais reste appliqué pour la session.
    }
    set({ mode });
  },
  toggle: () => get().setMode(get().mode === 'dark' ? 'light' : 'dark'),
}));

// Appliqué au chargement du module (avant le premier rendu) pour éviter un flash de mauvais thème.
applyToDocument(useThemeStore.getState().mode);
